import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const EventIntervalContext = createContext();

export const useEventInterval = () => {
  const context = useContext(EventIntervalContext);
  if (!context) {
    throw new Error('useEventInterval must be used within an EventIntervalProvider');
  }
  return context;
};

export const EventIntervalProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for event interval endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getIntervalBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/event-intervals/api/v1/event-intervals`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/event-intervals/api/v1/event-intervals`;
    } else {
      throw new Error('Invalid login type for event intervals access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access event intervals');
    }
  }, [loginType]);

  // ========================
  // INTERVAL CRUD OPERATIONS
  // ========================

  /**
   * Create a new recurring interval and generate all instances
   * @param {Object} intervalData - Interval configuration
   * @returns {Promise<Object>} Created interval with instances
   */
  const createInterval = useCallback(async (intervalData) => {
    validateAccess();
    
    if (!intervalData) {
      throw new Error('Interval data is required');
    }

    // Validate required fields
    const requiredFields = ['event_id', 'initial_date', 'event_repetitions'];
    const missingFields = requiredFields.filter(field => !intervalData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(intervalData.initial_date)) {
      throw new Error('Initial date must be in YYYY-MM-DD format');
    }

    // Validate frequency selection (exactly one must be true)
    const frequencies = ['is_daily', 'is_weekly', 'is_monthly', 'is_yearly'];
    const selectedFrequencies = frequencies.filter(f => intervalData[f] === true);
    
    if (selectedFrequencies.length === 0) {
      throw new Error('You must select one frequency (daily, weekly, monthly, or yearly)');
    }
    if (selectedFrequencies.length > 1) {
      throw new Error('Only one frequency type can be selected');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(intervalData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create recurring interval';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Get interval by interval ID
   * @param {number} intervalId - Interval ID
   * @returns {Promise<Object>} Interval details
   */
  const getIntervalById = useCallback(async (intervalId) => {
    validateAccess();
    
    if (!intervalId) {
      throw new Error('Interval ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${intervalId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get interval';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Get interval by event ID
   * @param {string} eventId - Parent event ID
   * @returns {Promise<Object>} Interval details
   */
  const getIntervalByEventId = useCallback(async (eventId) => {
    validateAccess();
    
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event/${encodeURIComponent(eventId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get interval for event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Update an existing interval (regenerates all instances)
   * @param {number} intervalId - Interval ID
   * @param {Object} updateData - Updated interval configuration
   * @returns {Promise<Object>} Updated interval with new instances
   */
  const updateInterval = useCallback(async (intervalId, updateData) => {
    validateAccess();
    
    if (!intervalId) {
      throw new Error('Interval ID is required');
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error('Update data is required');
    }

    // Validate date format if provided
    if (updateData.initial_date) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(updateData.initial_date)) {
        throw new Error('Initial date must be in YYYY-MM-DD format');
      }
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${intervalId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update interval';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Delete an interval and all its instances
   * @param {number} intervalId - Interval ID
   * @returns {Promise<void>}
   */
  const deleteInterval = useCallback(async (intervalId) => {
    validateAccess();
    
    if (!intervalId) {
      throw new Error('Interval ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${intervalId}`, {
        method: 'DELETE',
      });
      
      // 204 No Content response
      if (response.status === 204) {
        return { success: true, message: 'Interval deleted successfully' };
      }
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete interval';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  // ========================
  // INSTANCE OPERATIONS
  // ========================

  /**
   * Get all instances for an event
   * @param {string} eventId - Parent event ID
   * @returns {Promise<Array>} Array of event instances
   */
  const getInstancesByEventId = useCallback(async (eventId) => {
    validateAccess();
    
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event/${encodeURIComponent(eventId)}/instances`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get event instances';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Get future scheduled instances for an event
   * @param {string} eventId - Parent event ID
   * @returns {Promise<Array>} Array of future event instances
   */
  const getFutureInstancesByEventId = useCallback(async (eventId) => {
    validateAccess();
    
    if (!eventId) {
      throw new Error('Event ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/event/${encodeURIComponent(eventId)}/instances/future`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get future instances';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Get a specific instance by ID
   * @param {number} instanceId - Instance ID
   * @returns {Promise<Object>} Instance details
   */
  const getInstanceById = useCallback(async (instanceId) => {
    validateAccess();
    
    if (!instanceId) {
      throw new Error('Instance ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/instances/${instanceId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get instance';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Update a specific instance (status, overrides)
   * @param {number} instanceId - Instance ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated instance
   */
  const updateInstance = useCallback(async (instanceId, updateData) => {
    validateAccess();
    
    if (!instanceId) {
      throw new Error('Instance ID is required');
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error('Update data is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/instances/${instanceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update instance';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  /**
   * Cancel a specific instance
   * @param {number} instanceId - Instance ID
   * @returns {Promise<Object>} Cancelled instance
   */
  const cancelInstance = useCallback(async (instanceId) => {
    validateAccess();
    
    if (!instanceId) {
      throw new Error('Instance ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getIntervalBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/instances/${instanceId}/cancel`, {
        method: 'POST',
      });
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to cancel instance';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getIntervalBaseUrl]);

  // ========================
  // CONVENIENCE METHODS
  // ========================

  /**
   * Update instance status
   * @param {number} instanceId - Instance ID
   * @param {string} status - New status (scheduled, cancelled, completed, postponed)
   * @returns {Promise<Object>} Updated instance
   */
  const updateInstanceStatus = useCallback(async (instanceId, status) => {
    validateAccess();
    
    const validStatuses = ['scheduled', 'cancelled', 'completed', 'postponed'];
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
    }
    
    return await updateInstance(instanceId, { status });
  }, [updateInstance, validateAccess]);

  /**
   * Complete an instance
   * @param {number} instanceId - Instance ID
   * @returns {Promise<Object>} Updated instance
   */
  const completeInstance = useCallback(async (instanceId) => {
    validateAccess();
    return await updateInstanceStatus(instanceId, 'completed');
  }, [updateInstanceStatus, validateAccess]);

  /**
   * Bulk cancel multiple instances
   * @param {Array<number>} instanceIds - Array of instance IDs
   * @returns {Promise<Object>} Results of bulk operation
   */
  const bulkCancelInstances = useCallback(async (instanceIds) => {
    validateAccess();
    
    if (!instanceIds || instanceIds.length === 0) {
      throw new Error('Instance IDs array is required');
    }

    setLoading(true);
    setError(null);

    try {
      const cancelPromises = instanceIds.map(id => cancelInstance(id));
      const results = await Promise.allSettled(cancelPromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
      const failed = results.filter(r => r.status === 'rejected').map((r, idx) => ({
        instanceId: instanceIds[idx],
        error: r.reason.message,
      }));
      
      return {
        successful,
        failed,
        totalProcessed: instanceIds.length,
        successCount: successful.length,
        failureCount: failed.length,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk cancel instances';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [cancelInstance, validateAccess]);

  // ========================
  // HELPER METHODS
  // ========================

  /**
   * Get frequency display name
   * @param {Object} interval - Interval object
   * @returns {string} Human-readable frequency
   */
  const getFrequencyDisplay = useCallback((interval) => {
    if (interval.is_daily) return 'Daily';
    if (interval.is_weekly) return 'Weekly';
    if (interval.is_monthly) return 'Monthly';
    if (interval.is_yearly) return 'Yearly';
    return 'Unknown';
  }, []);

  /**
   * Format interval data for display
   * @param {Object} interval - Interval object
   * @returns {Object} Formatted interval
   */
  const formatIntervalData = useCallback((interval) => {
    const frequency = getFrequencyDisplay(interval);
    const startDate = new Date(interval.initial_date);
    const endDate = new Date(interval.end_date);
    
    return {
      ...interval,
      frequencyDisplay: frequency,
      formattedStartDate: startDate.toLocaleDateString(),
      formattedEndDate: endDate.toLocaleDateString(),
      durationInDays: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)),
    };
  }, [getFrequencyDisplay]);

  /**
   * Format instance data for display
   * @param {Object} instance - Instance object
   * @returns {Object} Formatted instance
   */
  const formatInstanceData = useCallback((instance) => {
    const instanceDate = new Date(instance.instance_date);
    const now = new Date();
    const isPast = instanceDate < now;
    const isFuture = instanceDate >= now;
    
    return {
      ...instance,
      formattedDate: instanceDate.toLocaleDateString(),
      formattedTime: instance.instance_time,
      isPast,
      isFuture,
      statusColor: 
        instance.status === 'scheduled' ? 'blue' :
        instance.status === 'completed' ? 'green' :
        instance.status === 'cancelled' ? 'red' :
        instance.status === 'postponed' ? 'orange' : 'gray',
      hasOverrides: !!(
        instance.override_title || 
        instance.override_description || 
        instance.override_location ||
        instance.override_guest_limit !== null ||
        instance.override_price !== null ||
        instance.override_cost !== null
      ),
    };
  }, []);

  /**
   * Validate interval creation data
   * @param {Object} intervalData - Interval data to validate
   * @returns {Array} Array of validation errors
   */
  const validateIntervalData = useCallback((intervalData) => {
    const errors = [];

    if (!intervalData.event_id) {
      errors.push({ field: 'event_id', message: 'Event ID is required' });
    }

    if (!intervalData.initial_date) {
      errors.push({ field: 'initial_date', message: 'Initial date is required' });
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(intervalData.initial_date)) {
        errors.push({ field: 'initial_date', message: 'Initial date must be in YYYY-MM-DD format' });
      }
    }

    if (!intervalData.event_repetitions || intervalData.event_repetitions < 1) {
      errors.push({ field: 'event_repetitions', message: 'Event repetitions must be at least 1' });
    }

    // Validate frequency caps
    if (intervalData.is_daily && intervalData.event_repetitions > 300) {
      errors.push({ field: 'event_repetitions', message: 'Daily repetitions cannot exceed 300' });
    }
    if (intervalData.is_weekly && intervalData.event_repetitions > 52) {
      errors.push({ field: 'event_repetitions', message: 'Weekly repetitions cannot exceed 52' });
    }
    if (intervalData.is_monthly && intervalData.event_repetitions > 12) {
      errors.push({ field: 'event_repetitions', message: 'Monthly repetitions cannot exceed 12' });
    }
    if (intervalData.is_yearly && intervalData.event_repetitions > 10) {
      errors.push({ field: 'event_repetitions', message: 'Yearly repetitions cannot exceed 10' });
    }

    // Check exactly one frequency is selected
    const frequencies = ['is_daily', 'is_weekly', 'is_monthly', 'is_yearly'];
    const selectedCount = frequencies.filter(f => intervalData[f] === true).length;
    
    if (selectedCount === 0) {
      errors.push({ field: 'frequency', message: 'You must select one frequency type' });
    }
    if (selectedCount > 1) {
      errors.push({ field: 'frequency', message: 'Only one frequency type can be selected' });
    }

    return errors;
  }, []);

  /**
   * Get endpoint info for debugging
   * @returns {Object} Endpoint information
   */
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: getIntervalBaseUrl(),
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
      availableOperations: {
        createInterval: 'POST /',
        getInterval: 'GET /{id}',
        updateInterval: 'PUT /{id}',
        deleteInterval: 'DELETE /{id}',
        getByEvent: 'GET /event/{eventId}',
        getInstances: 'GET /event/{eventId}/instances',
        getFutureInstances: 'GET /event/{eventId}/instances/future',
        getInstance: 'GET /instances/{instanceId}',
        updateInstance: 'PUT /instances/{instanceId}',
        cancelInstance: 'POST /instances/{instanceId}/cancel',
      },
    };
  }, [loginType, getIntervalBaseUrl]);

  const value = {
    // State
    loading,
    error,
    
    // ===== Interval Operations =====
    createInterval,
    getIntervalById,
    getIntervalByEventId,
    updateInterval,
    deleteInterval,
    
    // ===== Instance Operations =====
    getInstancesByEventId,
    getFutureInstancesByEventId,
    getInstanceById,
    updateInstance,
    cancelInstance,
    
    // ===== Convenience Methods =====
    updateInstanceStatus,
    completeInstance,
    bulkCancelInstances,
    
    // ===== Helpers =====
    clearError,
    getFrequencyDisplay,
    formatIntervalData,
    formatInstanceData,
    validateIntervalData,
    getEndpointInfo,
    
    // ===== Computed values =====
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasIntervalAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <EventIntervalContext.Provider value={value}>
      {children}
    </EventIntervalContext.Provider>
  );
};